import { useFilters } from "context/FilterContext";
import useEmblaCarousel from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { userRoute } from "pages/user/user.routes";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import BackgroundSection from "./BackgroundSection";

import bassImg from "assets/categories_images/bass.webp";
import celloImg from "assets/categories_images/cello.webp";
import djImg from "assets/categories_images/dj.webp";
import drumsImg from "assets/categories_images/drums.webp";
import guitarImg from "assets/categories_images/guitar.webp";
import pianoImg from "assets/categories_images/piano.webp";
import saxophoneImg from "assets/categories_images/saxophone.webp";
import singingImg from "assets/categories_images/singing.webp";
import trumpetImg from "assets/categories_images/trumpet.webp";
import violinImg from "assets/categories_images/violin.webp";
import { defaultThemeColors, mediaSizes, responsivePaddingX } from "ui/theme";

const categories = [
  { label: "Guitar", image: guitarImg },
  { label: "Bass", image: bassImg },
  { label: "Drums", image: drumsImg },
  { label: "Piano", image: pianoImg },
  { label: "Singing", image: singingImg },
  { label: "DJ", image: djImg },
  { label: "Violin", image: violinImg },
  { label: "Saxophone", image: saxophoneImg },
  { label: "Trumpet", image: trumpetImg },
  { label: "Cello", image: celloImg },
];

const OPTIONS = { align: "start", dragFree: true, duration: 40 };

export const Categories = () => {
  const navigate = useNavigate();
  const { filters, setFilters } = useFilters();
  const [emblaRef] = useEmblaCarousel(OPTIONS, [WheelGesturesPlugin()]);

  const handleClick = (label) => {
    setFilters({
      typeOfClass: { label, value: label },
    });
    navigate(userRoute.searchOnline, {
      state: {
        ...filters,
        typeOfClass: { label, value: label },
      },
    });
  };

  return (
    <BackgroundSectionStyled>
      <Scroller ref={emblaRef}>
        <Row>
          {categories.map((cat) => (
            <Card
              key={cat.label}
              onClick={() => handleClick(cat.label)}
              type="button"
              aria-label={cat.label}
            >
              <Icon>
                {cat.image && <IconImg src={cat.image} alt={cat.label} />}
              </Icon>
              <Label>{cat.label}</Label>
            </Card>
          ))}
        </Row>
      </Scroller>
    </BackgroundSectionStyled>
  );
};

const Scroller = styled.div`
  width: 100%;
  overflow: hidden;
  padding: 0 ${responsivePaddingX.desktop};
  box-sizing: border-box;

  @media (max-width: ${mediaSizes.lg}) {
    padding: 0 ${responsivePaddingX.lg};
  }

  @media (max-width: ${mediaSizes.md}) {
    padding: 0 ${responsivePaddingX.md};
  }

  @media (max-width: ${mediaSizes.xs}) {
    padding: 0 11px;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  touch-action: pan-y pinch-zoom;
  will-change: transform;
  backface-visibility: hidden;
  justify-content: space-between;
  @media (min-width: 730px) and (max-width: 800px) {
    flex-wrap: wrap;
  }
`;

const Card = styled.button`
  display: flex;
  flex-direction: column;
  -webkit-box-align: center;
  align-items: center;
  padding: 20px 12px;
  flex-shrink: 0;
  cursor: pointer;
  box-sizing: border-box;
  background: none;
  border-width: medium;
  border-style: none;
  border-color: currentcolor;
  border-image: initial;
  @media (max-width: 1080px) {
    padding: 20px 5px;
  }

  &:hover img,
  &:focus-visible img {
    transform: scale(1.08);
  }

  &:hover span,
  &:focus-visible span {
    color: ${({ theme }) => theme.colors.blue};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.blue};
    outline-offset: -4px;
    border-radius: 8px;
  }
`;

const Icon = styled.div`

  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #d9d9d9;
  overflow: hidden;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  @media (min-width: 800px) {
    width: calc((100vw - 160px) / 12);
    height: calc((100vw - 160px) / 12);
  }
  @media (min-width: 1080px) {
    width: calc((100vw - 160px) / 13);
    height: calc((100vw - 160px) / 13);
  }
  
`;

const IconImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  transition: transform 0.25s ease;
`;

const Label = styled.span`
    margin-top: 9px;
    font-size: clamp(14px,1.2vw,16px);
    font-weight: 400;
    color: ${defaultThemeColors.basicBlack};
    text-align: center;
    white-space: nowrap;
    letter-spacing: 0em;
    line-height: 1;
    transition: color 0.25s ease;
    font-family: var(--font-primary);
`;

const BackgroundSectionStyled = styled(BackgroundSection)`
  background-color: ${({ theme }) => theme.colors.secondary};
`;
